import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

class RBCartRecommendations extends Component {
  static observedAttributes = ['data-product-id', 'data-cart-product-ids', 'data-url', 'data-section-id'];

  #drawer;
  #activeRequest;
  #requestKey = '';
  #cache = new Map();
  #refreshQueued = false;

  connectedCallback() {
    super.connectedCallback();
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener(DialogOpenEvent.eventName, this.#queueRefresh);
    document.addEventListener(ThemeEvents.cartUpdate, this.#invalidateAndRefresh);
    this.#queueRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawer?.removeEventListener(DialogOpenEvent.eventName, this.#queueRefresh);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#invalidateAndRefresh);
    this.#activeRequest?.abort();
    this.#activeRequest = undefined;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#queueRefresh();
  }

  attributeChangedCallback(name, previous, next) {
    if (previous !== next) this.#invalidateAndRefresh();
  }

  #invalidateAndRefresh = () => {
    this.#activeRequest?.abort();
    this.#queueRefresh();
  };

  #queueRefresh = () => {
    if (!this.isConnected || this.#refreshQueued) return;
    this.#refreshQueued = true;
    queueMicrotask(() => {
      this.#refreshQueued = false;
      this.#loadRecommendations();
    });
  };

  #getRequestKey() {
    const { productId, cartProductIds, url, sectionId } = this.dataset;
    return JSON.stringify([productId, cartProductIds || '[]', url, sectionId]);
  }

  #loadRecommendations = async () => {
    if (!this.isConnected || !this.closest('dialog')?.open) return;

    const { productId, cartProductIds, url, sectionId } = this.dataset;
    if (!productId || !url || !sectionId) return;

    const requestKey = this.#getRequestKey();
    if (this.#activeRequest && !this.#activeRequest.signal.aborted && this.#requestKey === requestKey) return;
    this.#activeRequest?.abort();
    const controller = new AbortController();
    this.#activeRequest = controller;
    this.#requestKey = requestKey;

    try {
      const endpoint = new URL(url, window.location.origin);
      endpoint.searchParams.set('section_id', sectionId);
      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('limit', '10');
      endpoint.searchParams.set('intent', 'complementary');

      let markup = this.#cache.get(requestKey);
      if (markup === undefined) {
        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) return;
        markup = await response.text();
      }

      if (
        controller.signal.aborted ||
        !this.isConnected ||
        this.#requestKey !== requestKey ||
        this.#getRequestKey() !== requestKey
      ) return;
      this.#cache.set(requestKey, markup);

      const html = new DOMParser().parseFromString(markup, 'text/html');
      const responseCards = html.querySelectorAll('[data-rb-recommendation-response] [data-rb-recommendation-product-id]');
      const fallback = this.querySelector('template[data-rb-recommendation-fallback]');
      const fallbackCards = fallback instanceof HTMLTemplateElement
        ? fallback.content.querySelectorAll('[data-rb-recommendation-product-id]')
        : [];
      const excludedIds = new Set(JSON.parse(cartProductIds || '[]').map(String));
      const selected = [];

      for (const card of [...responseCards, ...fallbackCards]) {
        const id = card.getAttribute('data-rb-recommendation-product-id');
        if (!id || excludedIds.has(id)) continue;
        excludedIds.add(id);
        selected.push(card.cloneNode(true));
        if (selected.length === 4) break;
      }

      this.querySelector('[data-rb-recommendation-list]')?.replaceChildren(...selected);
      this.hidden = selected.length === 0;
    } catch (error) {
      if (error.name !== 'AbortError') this.#cache.delete(requestKey);
    } finally {
      if (this.#activeRequest === controller) this.#activeRequest = undefined;
    }
  };
}

if (!customElements.get('rb-cart-recommendations')) {
  customElements.define('rb-cart-recommendations', RBCartRecommendations);
}
